<template>
  <v-layout row class="designer-canvas pb-5" fill-height>
    <v-flex xs12 fill-height justify-center>
      <v-toolbar dense flat style="margin-bottom:10px">
        <v-tooltip bottom>
          <v-btn slot="activator" flat icon @click="workflowList">
            <v-icon>keyboard_arrow_left</v-icon>
          </v-btn>
          <span>Back</span>
        </v-tooltip>
        <v-toolbar-title class="body-2">
          Workflow Designer
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-tooltip bottom>
          <v-btn
            :loading="workflowUpdating"
            class="pa-1"
            icon
            @click.stop="showBotPopup = !showBotPopup"
            :color="showBotPopup ? 'accent' : ''"
            slot="activator"
          >
            <v-icon>info</v-icon>
          </v-btn>
          <span
            >{{ showBotPopup ? 'Hide' : 'Show' }} Bot information on double
            click
          </span>
        </v-tooltip>
        <v-tooltip bottom>
          <v-btn
            :loading="workflowUpdating"
            class="pa-1"
            icon
            @click.stop="workflowList"
            slot="activator"
          >
            <v-icon>playlist_play</v-icon>
          </v-btn>
          <span>Go-to Workflow List</span>
        </v-tooltip>
        <v-tooltip bottom>
          <v-btn
            :loading="workflowUpdating"
            class="pa-1"
            icon
            @click.stop="saveDialog"
            slot="activator"
          >
            <v-icon>save</v-icon>
          </v-btn>
          <span>Save BPMN Diagram</span>
        </v-tooltip>
        <v-tooltip bottom>
          <v-btn
            :loading="workflowUpdating"
            class="pa-1"
            icon
            @click.stop="saveXML"
            slot="activator"
          >
            <v-icon>get_app</v-icon>
          </v-btn>
          <span>Download Diagram as XML</span>
        </v-tooltip>
        <v-tooltip bottom>
          <v-btn
            :loading="workflowUpdating"
            class="pa-1"
            icon
            @click.stop="saveSVG"
            slot="activator"
          >
            <v-icon>image</v-icon>
          </v-btn>
          <span>Download Diagram as SVG</span>
        </v-tooltip>
      </v-toolbar>
      <v-progress-linear
        indeterminate
        color="accent"
        class="ma-0"
        height="5"
        :active="workflowUpdating"
      />
      <v-btn
        fab
        bottom
        class="create-bpmn"
        color="secondary"
        v-if="isWorkflowMain"
        @click="initNewBPMN"
      >
        <v-icon :color="$vuetify.dark ? '#000' : ''">add</v-icon>
      </v-btn>
      <dropzone
        style="height: 95%"
        id="workflowDropzone"
        v-if="isWorkflowMain"
        :options="dropzoneOptions"
        ref="dropzone"
        @vdropzone-file-added="dropzoneFileAdded"
      >
      </dropzone>
      <workflow-widget
        v-else
        :bpmn-xml="currentWorkflow.data"
        zoom-reset-button
        zoom-in-button
        zoom-out-button
        full-screen-toggle
        properties-panel
        minimap-toggle
        :editable="editMode"
        ref="workflowWidget"
        :tag-workflow-items="tagWorkflowItems"
        :provider-modules="propertiesProviderModules"
        :moddle-descriptors="moddleDescriptors"
        @element_dblclick="handleSelection"
      >
      </workflow-widget>
      <v-dialog v-model="workflowPropertiesDialog" persistent max-width="600">
        <v-card class="beez-card">
          <v-toolbar dense class="beez-bold">
            Enter workflow properties
          </v-toolbar>
          <v-container>
            <v-text-field
              color="secondary"
              label="Name"
              autofocus
              required
              v-model="name"
            />
            <v-text-field
              color="secondary"
              label="Description"
              v-model="description"
              multi-line
              required
              :rows="2"
              auto-grow
            />
          </v-container>
          <v-card-actions>
            <v-spacer />
            <v-btn flat icon @click="workflowPropertiesDialog = false">
              <v-icon>close</v-icon>
            </v-btn>
            <v-btn flat icon class="primary" @click="save">
              <v-icon>check</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-flex>
    <v-dialog v-model="showBotInfoDialog" persistent max-width="900px">
      <v-card class="beez-card">
        <v-toolbar dense class="beez-bold">
          Bot information
          <v-spacer></v-spacer>
          <v-btn icon @click="showBotInfoDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <bot-card :hex-id="botId"></bot-card>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import Dropzone from 'vue2-dropzone'
import { saveAs } from 'file-saver/FileSaver'
import WorkflowWidget from '@/components/common/WorkflowWidget'
import camundaProviderModule from 'bpmn-js-properties-panel/lib/provider/camunda'
import botProviderModule from '@/components/workflow-management/bpmn-custom/provider/bot'
import botModdleDescriptor from '@/components/workflow-management/bpmn-custom/descriptors/bot'
import camundaModdleDescriptor from 'camunda-bpmn-moddle/resources/camunda'
import BotCard from '@/components/bot/BotCard.vue'
import moment from 'moment'
import u from '@/utils'
import { mapState } from 'vuex'

export default {
  name: 'workflow-designer',
  props: {
    workflow: Object,
    hexId: String
  },

  data() {
    return {
      showBotInfoDialog: false,
      botId: null,
      tagWorkflowItems: true,
      editMode: true,
      currentWorkflow: {},
      workflowUpdating: false,
      showBotPopup: false,
      dropzoneOptions: {
        maxFiles: 1,
        url: 'https://tulip.beezlabs.com',
        dictDefaultMessage: this.defaultMessage(),
        autoProcessQueue: false,
        previewTemplate: this.dropzoneTemplate(),
        acceptedFiles: '.bpmn'
      },
      extraButtons: [
        {
          title: 'Workflow List',
          method: () => {
            this.workflowList()
          },
          icon: 'playlist_play'
        },
        {
          title: 'Save BPMN Diagram',
          method: () => {
            this.saveDialog()
          },
          icon: 'save'
        },
        {
          title: 'Download Diagram as XML',
          method: () => {
            this.saveXML()
          },
          icon: 'get_app'
        },
        {
          title: 'Download Diagram as SVG',
          method: () => {
            this.saveSVG()
          },
          icon: 'image'
        }
      ],
      name: '',
      description: '',
      workflowPropertiesDialog: false,
      propertiesProviderModules: [camundaProviderModule, botProviderModule],
      moddleDescriptors: {
        camunda: camundaModdleDescriptor,
        bot: botModdleDescriptor
      }
    }
  },

  mounted() {
    this.getHiveTypes()
    if (this.hives && this.hives.length === 0) {
      this.getHives()
    }
  },

  computed: {
    ...mapState({
      hiveTypes: state => state.enums.hiveTypes,
      hives: state => state.hives.hives
    }),
    isWorkflowMain() {
      return this.$route.name === 'workflow-designer'
    },
    isWorkflowDetail() {
      return this.$route.name === 'workflow-designer-detail'
    },
    isWorkflowCreate() {
      return this.$route.name === 'workflow-designer-create'
    }
  },

  methods: {
    handleSelection(event) {
      if (
        this.showBotPopup &&
        event.element &&
        event.type === 'element.dblclick' &&
        event.element.businessObject &&
        event.element.businessObject.$type === 'bpmn:ServiceTask'
      ) {
        if (event.element.businessObject.bot) {
          this.botId = event.element.businessObject.bot
          this.showBotInfoDialog = true
        }
      }
    },
    async getHives() {
      this.botUpdating = true
      try {
        await this.$store.dispatch('fetchHives')
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Unable to fetch Agent',
          description: e.response
            ? e.response.data.message
            : 'Unable to fetch Agent',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      this.botUpdating = false
    },
    async getHiveTypes() {
      this.botUpdating = true
      try {
        await this.$store.dispatch('fetchHiveTypes')
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Unable to fetch Agent Type',
          description: e.response
            ? e.response.data.message
            : 'Unable to fetch Agent Type',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      this.botUpdating = false
    },

    async initNewBPMN() {
      this.$router
        .push({
          name: 'workflow-designer-create'
        })
        .catch()
      let diagram = await this.$store.dispatch('fetchDefaultWorkflow')
      this.updateCurrentWorkflowData(diagram)
    },

    dropzoneTemplate() {
      return `<div class="dz-preview dz-file-preview">
                <div class="dz-details">
                  <div class="dz-filename">Now uploading<br><br><div data-dz-name></div></div>
                  <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
                </div>
              </div>`
    },

    defaultMessage() {
      return `<div>
                <div>Drag and Drop the BPMN file or click to upload</div>
                <div class="upload"><i class="v-icon material-icons">cloud_upload</i></div>
              </div>`
    },

    dropzoneFileAdded(file) {
      let fileReader = new FileReader()
      fileReader.onload = event => {
        this.updateCurrentWorkflowData(event.target.result)
        this.$router.push({
          name: 'workflow-designer-create',
          params: { workflow: { data: event.target.result } }
        })
      }
      fileReader.onerror = () => {
        this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Unable to load Workflow',
          description: fileReader.error.message,
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      fileReader.onabort = () => {
        this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Aborted loading the Workflow',
          description: 'Workflow load was aborted before it could be completed',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      fileReader.onprogress = event => {
        if (event.lengthComputable) {
          this.$refs.dropzone.dropzone.emit(
            'uploadprogress',
            file,
            (event.loaded / event.total) * 100,
            event.loaded
          )
        }
      }
      fileReader.readAsText(file)
      this.$refs.dropzone.removeAllFiles(true)
    },

    updateCurrentWorkflowData(xmlData) {
      this.currentWorkflow = {}
      this.currentWorkflow.data = xmlData
    },

    workflowList() {
      this.$router.push({
        name: 'workflow-detail',
        params: { hexId: this.currentWorkflow.hexId }
      })
    },

    async saveDialog() {
      if (this.isWorkflowCreate) {
        this.workflowPropertiesDialog = true
      } else {
        this.save()
      }
    },

    save() {
      this.$refs.workflowWidget
        .getBpmnCanvas()
        .saveXML({ format: true }, async (err, xml) => {
          if (!err) {
            if (this.isWorkflowDetail) {
              this.workflowUpdating = true
              try {
                let response = await this.$store.dispatch('updateWorkflow', {
                  body: { data: xml },
                  hexId: this.hexId
                })
                this.currentWorkflow = response
                await this.$store.dispatch('directNotification', {
                  type: 'success',
                  message: 'Workflow saved Successfully',
                  description: `The workflow ${this.currentWorkflow.hexId} has been saved.`,
                  timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
                })
              } catch (e) {
                await this.$store.dispatch('directNotification', {
                  type: 'error',
                  message: 'Unable to save Workflow',
                  description: e.response
                    ? e.response.data.message
                    : 'Error unknown! Kindly retry',
                  timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
                })
              }
              this.workflowUpdating = false
            } else if (this.isWorkflowCreate) {
              this.workflowUpdating = true
              try {
                let response = await this.$store.dispatch('saveWorkflow', {
                  name: this.name,
                  description: this.description,
                  data: xml
                })
                this.$store.dispatch('fetchWorkflows')
                this.$router.replace({
                  name: 'workflow-designer-detail',
                  params: {
                    workflow: response,
                    hexId: response.hexId
                  }
                })
                this.currentWorkflow = response
                await this.$store.dispatch('directNotification', {
                  type: 'success',
                  message: 'Workflow saved Successfully',
                  description:
                    'The workflow ' +
                    this.currentWorkflow.hexId +
                    ' has been saved.',
                  timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
                })
              } catch (e) {
                await this.$store.dispatch('directNotification', {
                  type: 'error',
                  message: 'Unable to save Workflow',
                  description: e.response
                    ? e.response.data.message
                    : 'Error unknown! Kindly retry',
                  timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
                })
              }
              this.workflowUpdating = false
            }
          } else {
            await this.$store.dispatch('directNotification', {
              type: 'error',
              message: 'Unable to save Workflow',
              description: err.toString(),
              timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
            })
          }
          this.workflowPropertiesDialog = false
        })
    },

    saveXML() {
      this.$refs.workflowWidget
        .getBpmnCanvas()
        .saveXML({ format: true }, (err, xml) => {
          if (!err) {
            saveAs(
              new Blob([xml], { type: 'text/xml;charset=utf-8' }),
              'workflow.bpmn'
            )
          }
        })
    },

    saveSVG() {
      this.$refs.workflowWidget.getBpmnCanvas().saveSVG((err, svg) => {
        if (!err) {
          saveAs(
            new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }),
            'workflow.svg'
          )
        }
      })
    }
  },

  async created() {
    if (this.isWorkflowDetail) {
      if (this.workflow) {
        this.currentWorkflow = u.clone(this.workflow)
      } else {
        try {
          this.currentWorkflow = await this.$store.dispatch(
            'fetchWorkflow',
            this.hexId
          )
        } catch (e) {
          await this.$store.dispatch('directNotification', {
            type: 'error',
            message: 'Unable to fetch Workflow',
            description: e.response
              ? e.response.data.message
              : 'Error unknown! Kindly retry',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
        }
      }
    } else if (this.isWorkflowCreate) {
      if (this.workflow && this.workflow.data) {
        this.updateCurrentWorkflowData(this.workflow.data)
      } else {
        let diagram = await this.$store.dispatch('fetchDefaultWorkflow')
        this.updateCurrentWorkflowData(diagram)
      }
    }
  },

  components: {
    WorkflowWidget,
    BotCard,
    dropzone: Dropzone
  }
}
</script>
<style scoped>
.designer-canvas {
  min-height: 400px;
}
</style>

<style>
.create-bpmn {
  position: absolute !important;
  right: 40px;
}

.dz-message {
  height: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  font-size: 2em;
}

.dz-preview {
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  align-items: center !important;
}

.dz-details {
  flex: 0 1 auto !important;
  top: unset !important;
  bottom: unset !important;
  left: unset !important;
  right: unset !important;
  min-width: 250px !important;
  min-height: 250px !important;
  display: inline-block !important;
}

.dz-filename {
  font-size: 2em !important;
  text-align: center !important;
  line-height: 1.5em !important;
}

.dz-progress {
  bottom: 20px !important;
  width: 80% !important;
  top: unset !important;
  left: 0 !important;
  right: 0 !important;
  margin: auto !important;
  animation: unset !important;
}

.upload i {
  font-size: 2em;
}
</style>
